export type ProductRole = "OWNER" | "MANAGER" | "CONTRIBUTOR" | "VIEWER";

export interface ProductBusiness {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  memberId: string;
  role: ProductRole;
}

export interface ProductSession {
  account: { id: string };
  member: { id: string; displayName: string; role: ProductRole };
  business: Pick<ProductBusiness, "id" | "slug" | "name" | "timezone">;
}

export interface ProductProviderConnection {
  id: string;
  workspaceId: string;
  provider: "WHATSAPP_BAILEYS" | "WHATSAPP_CLOUD" | "X" | "XAI_GROK" | "RESEND";
  service: "MESSAGING" | "SOCIAL_PUBLISHING" | "CONTENT_MODEL" | "EMAIL";
  externalAccountRef: string;
  displayName: string;
  credentialConfigured: boolean;
  scopes: readonly string[];
  capabilities: readonly ("MESSAGES_READ" | "MESSAGES_SEND" | "SOCIAL_DRAFT" | "SOCIAL_PUBLISH" | "EMAIL_SEND")[];
  status: "PENDING" | "CONNECTED" | "UNAVAILABLE" | "REVOKED";
  unavailableReason: string | null;
  maxRequestsPerWindow: number;
  windowSeconds: number;
  dailyCostLimitMicros: number;
  estimatedCostMicrosPerRequest: number;
  version: number;
  lastVerifiedAt: string | null;
  revokedAt: string | null;
}

export interface ProductDepartment {
  id: string;
  name: string;
  description: string;
  version: number;
}

export interface ProductDepartmentAccess {
  memberId: string;
  departmentId: string;
  accessScope: "OWN_RECORDS" | "ASSIGNED_RECORDS" | "DEPARTMENT_RECORDS";
  accessLevel: "READ" | "CONTRIBUTE" | "MANAGE";
  version: number;
}

export interface ProductSocialPublication {
  id: string;
  workspaceId: string;
  publishConnectionId: string;
  draftConnectionId: string | null;
  state: "DRAFT" | "AWAITING_APPROVAL" | "APPROVED" | "SCHEDULED" | "PUBLISHING" | "PUBLISHED" | "FAILED" | "RECONCILIATION_REQUIRED" | "CANCELLED";
  revision: number;
  version: number;
  body: string;
  scheduledFor: string | null;
  contentHash: string;
  approvedBy: string | null;
  approvedAt: string | null;
  providerPostId: string | null;
}


// WHY [BOL-MULTI-ACCOUNT-SUBSCRIPTION-001]: browser projections of the paid
// multi-account records. They mirror the server records exactly; the browser
// never decides trial, payment or routing authority itself.
export type ProductSubscriptionState = "TRIAL" | "ACTIVE" | "PAST_DUE" | "CANCELLED";
export type ProductInvoiceState = "REQUESTED" | "ISSUED" | "PROOF_SUBMITTED" | "MATCHED" | "REJECTED" | "VOID";
export type ProductInstanceState = "PENDING_PROVISION" | "PROVISIONED" | "RUNNING" | "PAUSED" | "RETIRED";
// WHY [BOL-SELF-SERVE-WEB-002]: the root provisioner may defer a pending
// workspace (trial cap reached, or not enough database room). The browser shows
// that truthfully instead of an endless generic spinner. Removing this makes a
// deferred signup indistinguishable from a broken one.
export type ProductRuntimeWaitReason = "CAPACITY" | "DB_CONNECTION_BUDGET";
export type ProductTrialLimitKind = "TIME" | "USAGE" | "CREDIT";

export interface ProductTrialLimit {
  capabilityKey: string;
  limitKind: ProductTrialLimitKind;
  limitUnit: "SECONDS" | "ANSWERS" | "MINUTES" | "JOBS" | "CREDITS";
  limitValue: number | null;
  version: number;
}

export interface ProductTrialComponent {
  capabilityKey: string;
  displayName: string;
  description: string;
  displayOrder: number;
  limits: readonly ProductTrialLimit[];
  state: "NOT_STARTED" | "ACTIVE" | "REACHED" | "LOCKED";
  startedAt: string | null;
  endsAt: string | null;
  usedUnits: number;
  version: number;
}

export interface ProductInvoice {
  id: string;
  workspaceId: string;
  invoiceNumber: string;
  planKey: string;
  months: number;
  amountIdr: number | null;
  description: string;
  state: ProductInvoiceState;
  transferReference: string | null;
  proofNote: string;
  issuedAt: string | null;
  proofSubmittedAt: string | null;
  matchedAt: string | null;
  rejectionReason: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  version: number;
  createdAt: string;
}

export interface ProductRuntimeInstance {
  id: string;
  workspaceId: string;
  hostname: string | null;
  state: ProductInstanceState;
  apiPort: number | null;
  connectorInternalUrl: string | null;
  authNamespace: string | null;
  provisionedAt: string | null;
  lastHealthAt: string | null;
  version: number;
  // Optional: an older API omits it; absent means "no known wait reason".
  waitReason?: ProductRuntimeWaitReason | null;
}

export interface ProductPaymentInstructions {
  bankName: string;
  accountName: string;
  accountNumber: string;
  instructions: string;
  version: number;
}

export interface ProductSubscriptionSnapshot {
  platformOperator: boolean;
  subscription: {
    id: string; workspaceId: string; planKey: string; state: ProductSubscriptionState; platformOwned: boolean;
    trialDisclosedAt: string | null; currentPeriodStart: string | null; currentPeriodEnd: string | null;
    activatedAt: string | null; cancelledAt: string | null; version: number;
    // WHY [BOL-SELF-SERVE-WEB-002]: the three-day trial clock starts at the
    // first WhatsApp connection the database confirms (R707/R739); direct-beta
    // businesses are untimed (R769). These fields are optional so a
    // browser served before the API update renders no trial state instead of a
    // false countdown. The server still enforces every trial boundary.
    trialClockEligible?: boolean;
    trialStartedAt?: string | null;
    trialEndsAt?: string | null;
    directBeta?: boolean;
  };
  plan: { planKey: string; displayName: string; monthlyPriceIdr: number | null; version: number };
  trial: readonly ProductTrialComponent[];
  invoices: readonly ProductInvoice[];
  paymentInstructions: ProductPaymentInstructions | null;
  runtimeInstance: ProductRuntimeInstance | null;
}

export interface ProductPendingAccount {
  id: string;
  email: string;
  displayName: string;
  createdAt: string;
}

export interface ProductSignupLegalAcceptance {
  termsAccepted: true;
  privacyAcknowledged: true;
  releaseId: string;
  termsVersion: string;
  termsSha256: string;
  privacyVersion: string;
  privacySha256: string;
}
export type ProductSignupPolicy = { available: false } | {
  available: true;
  releaseId: string;
  terms: { version: string; sha256: string; url: string };
  privacy: { version: string; sha256: string; url: string };
};

export interface ProductApi {
  signupPolicy(): Promise<ProductSignupPolicy>;
  signup(input: {
    email: string;
    password: string;
    displayName: string;
    businessName: string;
    businessSlug: string;
    timezone?: string;
    legalAcceptance: ProductSignupLegalAcceptance;
  }, requestKey?: string): Promise<{ accepted: true; confirmationRequired: true; deliveryAdapter: "CONFIGURED" | "UNAVAILABLE" }>;
  confirmEmail(token: string): Promise<{ confirmed: true }>;
  confirmEmailCode(email: string, code: string): Promise<{ confirmed: true }>;
  login(email: string, password: string, businessSlug?: string): Promise<ProductSession | {
    selectionRequired: true;
    businesses: readonly ProductBusiness[];
  }>;
  session(): Promise<ProductSession>;
  logout(): Promise<void>;
  businesses(): Promise<{ businesses: readonly ProductBusiness[] }>;
  createBusiness(input: { name: string; slug: string; timezone?: string }, requestKey?: string): Promise<{ business: ProductBusiness }>;
  connections(): Promise<{ connections: readonly ProductProviderConnection[] }>;
  registerConnection(input: {
    provider: ProductProviderConnection["provider"];
    externalAccountRef: string;
    displayName: string;
    credentialRef?: string | null;
    scopes?: readonly string[];
    maxRequestsPerWindow?: number;
    windowSeconds?: number;
    dailyCostLimitMicros?: number;
    estimatedCostMicrosPerRequest?: number;
  }, requestKey?: string): Promise<{ connection: ProductProviderConnection }>;
  revokeConnection(id: string, expectedVersion: number, requestKey?: string): Promise<{ connection: ProductProviderConnection }>;
  createDepartment(input: { name: string; description?: string }, requestKey?: string): Promise<{ department: ProductDepartment }>;
  setDepartmentAccess(input: {
    memberId: string;
    departmentId: string;
    accessScope: ProductDepartmentAccess["accessScope"];
    accessLevel: ProductDepartmentAccess["accessLevel"];
    expectedVersion?: number | null;
  }, requestKey?: string): Promise<{ access: ProductDepartmentAccess }>;
  createSocialDraft(input: {
    publishConnectionId: string;
    draftConnectionId?: string | null;
    body: string;
    scheduledFor?: string | null;
    reason?: string;
  }, requestKey?: string): Promise<{ publication: ProductSocialPublication }>;
  requestSocialApproval(id: string, expectedRevision: number, requestKey?: string): Promise<{ publication: ProductSocialPublication }>;
  approveSocial(id: string, expectedRevision: number, requestKey?: string): Promise<{ publication: ProductSocialPublication }>;
  publishSocial(id: string): Promise<{ publication: ProductSocialPublication }>;
  subscription(): Promise<{ subscription: ProductSubscriptionSnapshot }>;
  acknowledgeTrialDisclosure(requestKey?: string): Promise<{ subscription: ProductSubscriptionSnapshot["subscription"] }>;
  requestActivation(input: { planKey?: string; months: number }, requestKey?: string): Promise<{ invoice: ProductInvoice }>;
  submitTransferProof(id: string, input: { transferReference: string; note: string; expectedVersion: number }, requestKey?: string): Promise<{ invoice: ProductInvoice }>;
  requestRuntimeInstance(requestKey?: string): Promise<{ instance: ProductRuntimeInstance }>;
  selectInstance(slug: string): Promise<{ instance: { slug: string; state: ProductInstanceState } }>;
  clearInstance(): Promise<void>;
  platformInvoices(): Promise<{ invoices: readonly ProductInvoice[] }>;
  issueInvoice(id: string, input: { amountIdr: number; description: string; expectedVersion: number }, requestKey?: string): Promise<{ invoice: ProductInvoice }>;
  matchInvoice(id: string, input: { periodStart?: string; expectedVersion: number }, requestKey?: string): Promise<{ invoice: ProductInvoice; subscription: ProductSubscriptionSnapshot["subscription"] }>;
  rejectInvoice(id: string, input: { reason: string; expectedVersion: number }, requestKey?: string): Promise<{ invoice: ProductInvoice }>;
  setTrialLimit(input: { capabilityKey: string; limitKind: ProductTrialLimitKind; limitValue: number | null; expectedVersion: number }, requestKey?: string): Promise<{ limit: ProductTrialLimit }>;
  setPlanPrice(input: { planKey: string; monthlyPriceIdr: number | null; expectedVersion: number }, requestKey?: string): Promise<{ plan: ProductSubscriptionSnapshot["plan"] }>;
  setPaymentInstructions(input: { bankName: string; accountName: string; accountNumber: string; instructions: string; expectedVersion: number | null }, requestKey?: string): Promise<{ paymentInstructions: ProductPaymentInstructions }>;
  platformInstances(): Promise<{ instances: readonly ProductRuntimeInstance[] }>;
  pendingAccounts(): Promise<{ accounts: readonly ProductPendingAccount[] }>;
  confirmAccount(id: string, requestKey?: string): Promise<{ account: { accountId: string; state: "ACTIVE" } }>;
}

export function createProductApi(fetchImplementation: typeof fetch = fetch): ProductApi {
  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const response = await fetchImplementation(path, {
      ...init,
      credentials: "same-origin",
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers
      }
    });
    const payload: unknown = await response.json();
    if (!response.ok) {
      const error = isRecord(payload) && isRecord(payload.error) ? payload.error : {};
      throw Object.assign(new Error(typeof error.message === "string" ? error.message : "Product request failed."), {
        status: response.status,
        code: typeof error.code === "string" ? error.code : "REQUEST_FAILED",
        // WHY: signup must show the server's rejected fields instead of an
        // unaccompanied "highlighted information" banner. Never retain values.
        issues: Array.isArray(error.issues) ? error.issues.map((issue: unknown) => isRecord(issue) ? { path: issue.path } : {}) : [],
        retryAfterSeconds: response.status === 429 ? parseRetryAfter(response.headers.get("Retry-After")) : undefined
      });
    }
    return payload as T;
  };

  const mutationHeaders = (requestKey?: string) => ({ "Idempotency-Key": requestKey ?? crypto.randomUUID() });
  return {
    signupPolicy: () => request("/api/product/auth/signup-policy"),
    signup: (input, requestKey) => request("/api/product/auth/signup", {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    confirmEmail: (token) => request("/api/product/auth/confirm", {
      method: "POST", body: JSON.stringify({ token })
    }),
    confirmEmailCode: (email, code) => request("/api/product/auth/confirm-code", {
      method: "POST", body: JSON.stringify({ email, code })
    }),
    login: (email, password, businessSlug) => request("/api/product/auth/login", {
      method: "POST", body: JSON.stringify({ email, password, ...(businessSlug ? { businessSlug } : {}) })
    }),
    session: () => request("/api/product/session"),
    logout: async () => {
      const response = await fetchImplementation("/api/product/auth/logout", { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw Object.assign(new Error("The product session could not be signed out."), { status: response.status, code: "REQUEST_FAILED" });
    },
    businesses: () => request("/api/product/businesses"),
    createBusiness: (input, requestKey) => request("/api/product/businesses", {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    connections: () => request("/api/product/connections"),
    registerConnection: (input, requestKey) => request("/api/product/connections", {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    revokeConnection: (id, expectedVersion, requestKey) => request(`/api/product/connections/${encodeURIComponent(id)}/revoke`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify({ expectedVersion })
    }),
    createDepartment: (input, requestKey) => request("/api/product/departments", {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify({
        name: input.name,
        description: input.description ?? ""
      })
    }),
    setDepartmentAccess: (input, requestKey) => request("/api/product/departments/access", {
      method: "PUT", headers: mutationHeaders(requestKey), body: JSON.stringify({
        ...input,
        expectedVersion: input.expectedVersion ?? null
      })
    }),
    createSocialDraft: (input, requestKey) => request("/api/product/social", {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    requestSocialApproval: (id, expectedRevision, requestKey) => request(`/api/product/social/${encodeURIComponent(id)}/request-approval`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify({ expectedRevision })
    }),
    approveSocial: (id, expectedRevision, requestKey) => request(`/api/product/social/${encodeURIComponent(id)}/approve`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify({ expectedRevision })
    }),
    publishSocial: (id) => request(`/api/product/social/${encodeURIComponent(id)}/publish`, { method: "POST" }),
    subscription: () => request("/api/product/subscription"),
    acknowledgeTrialDisclosure: (requestKey) => request("/api/product/subscription/disclosure", { method: "POST", headers: mutationHeaders(requestKey) }),
    requestActivation: (input, requestKey) => request("/api/product/subscription/activation-requests", {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    submitTransferProof: (id, input, requestKey) => request(`/api/product/subscription/invoices/${encodeURIComponent(id)}/proof`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    requestRuntimeInstance: (requestKey) => request("/api/product/runtime-instance", { method: "POST", headers: mutationHeaders(requestKey) }),
    selectInstance: (slug) => request("/api/product/instances/select", { method: "POST", body: JSON.stringify({ slug }) }),
    clearInstance: async () => {
      const response = await fetchImplementation("/api/product/instances/select", { method: "DELETE", credentials: "same-origin" });
      if (!response.ok) throw Object.assign(new Error("The runtime selection could not be cleared."), { status: response.status, code: "REQUEST_FAILED" });
    },
    platformInvoices: () => request("/api/product/platform/invoices"),
    issueInvoice: (id, input, requestKey) => request(`/api/product/platform/invoices/${encodeURIComponent(id)}/issue`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    matchInvoice: (id, input, requestKey) => request(`/api/product/platform/invoices/${encodeURIComponent(id)}/match`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    rejectInvoice: (id, input, requestKey) => request(`/api/product/platform/invoices/${encodeURIComponent(id)}/reject`, {
      method: "POST", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    setTrialLimit: (input, requestKey) => request("/api/product/platform/trial-limits", {
      method: "PUT", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    setPlanPrice: (input, requestKey) => request("/api/product/platform/plans/price", {
      method: "PUT", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    setPaymentInstructions: (input, requestKey) => request("/api/product/platform/payment-instructions", {
      method: "PUT", headers: mutationHeaders(requestKey), body: JSON.stringify(input)
    }),
    platformInstances: () => request("/api/product/platform/runtime-instances"),
    pendingAccounts: () => request("/api/product/platform/accounts"),
    confirmAccount: (id, requestKey) => request(`/api/product/platform/accounts/${encodeURIComponent(id)}/confirm`, {
      method: "POST", headers: mutationHeaders(requestKey)
    })
  };
}

export const productApi = createProductApi();

export async function signOutWorkspace({
  apiClient, legacyLogout
}: {
  apiClient: Pick<ProductApi, "session" | "clearInstance" | "logout">;
  legacyLogout: () => Promise<void>;
}): Promise<"PRODUCT" | "LEGACY"> {
  try {
    await apiClient.session();
  } catch (cause) {
    // WHY [BOL-PRODUCT-SIGNOUT-001]: only an authoritative 401 identifies a
    // legacy session. Network/routing failures must not sign out the wrong
    // workspace or make the UI claim a successful sign-out.
    if (!isRecord(cause) || cause.status !== 401) throw cause;
    await legacyLogout();
    return "LEGACY";
  }
  // WHY [BOL-PRODUCT-SIGNOUT-002]: Caddy routes every path by bo_instance.
  // Clear it while product auth is valid, then invalidate the shared session
  // on the platform route. Reversing these calls strands the browser on the
  // rental runtime and breaks the original Agency short-login front door.
  await apiClient.clearInstance();
  await apiClient.logout();
  return "PRODUCT";
}

// WHY [BOL-SELF-SERVE-WEB-002]: sign-in must branch on the server's typed
// runtime and trial codes (for example RUNTIME_INSTANCE_MISSING), never on
// message text. These helpers read only the code/status the request owner
// attaches; anything else is null so callers fail closed to a plain error.
export type ProductErrorCode =
  | "RUNTIME_INSTANCE_MISSING" | "RUNTIME_INSTANCE_NOT_RUNNING" | "RUNTIME_INSTANCE_FORBIDDEN"
  | "SUBSCRIPTION_CANCELLED" | "TRIAL_WHATSAPP_NOT_CONNECTED" | "TRIAL_EXPIRED" | "LUNA_TEMPLATE_UNAVAILABLE";

export function productErrorCode(cause: unknown): string | null {
  return isRecord(cause) && typeof cause.code === "string" ? cause.code : null;
}

export function productErrorStatus(cause: unknown): number | null {
  return isRecord(cause) && typeof cause.status === "number" ? cause.status : null;
}

export function isProductErrorCode(cause: unknown, code: ProductErrorCode): boolean {
  return productErrorCode(cause) === code;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined;
  const numeric = Number(value);
  const seconds = Number.isFinite(numeric) ? numeric : (Date.parse(value) - Date.now()) / 1000;
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : undefined;
}
